import * as React from 'react'
import { ToggleState, useToggleState } from './use-toggle-state'
import { matchesSearch } from '@basehub/data/replicache/helpers/search'

export type ComboboxOption = {
  label: string
  value: string
  keywords?: string[]
  disabled?: boolean
}

export type ComboboxStateParams<OptionType extends ComboboxOption> = {
  options: OptionType[]
  onSelect: (option: OptionType) => void
  selectedOptionDataAtrribute: `data-${string}`
  initialOpenState?: boolean
  alwaysOpen?: boolean
  defaultOptions?: OptionType[]
  getOptions?: (params: {
    options: OptionType[]
    search: string
  }) => OptionType[]
  onClose?: () => void
  onOpen?: () => void
}

export const useComboboxState = <
  ListType extends HTMLElement,
  OptionType extends ComboboxOption,
>({
  options,
  onSelect,
  initialOpenState,
  alwaysOpen,
  selectedOptionDataAtrribute,
  defaultOptions,
  getOptions,
  onClose,
  onOpen,
}: ComboboxStateParams<OptionType>) => {
  const toggleState = useToggleState(initialOpenState)
  const isOpen = alwaysOpen ? true : toggleState.isOn

  const [search, setSearch] = React.useState('')
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [isPointerIdle, setIsPointerIdle] = React.useState(true)

  const listRef = React.useRef<ListType>(null)
  const listScrollerRef = React.useRef<HTMLDivElement>(null)

  // handle pointer idle stuff
  React.useEffect(() => {
    if (!isOpen) {
      setIsPointerIdle(true)
      return
    }

    const handleMouseMove = () => {
      setIsPointerIdle(false)
    }
    const handleKeyDown = () => {
      setIsPointerIdle(true)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(0)
      setSearch('')
    } else if (!listScrollerRef.current) {
      const scroller = listRef.current?.querySelector(
        '[data-radix-scroll-area-viewport]'
      )
      if (!scroller) return
      listScrollerRef.current = scroller as HTMLDivElement
    }
  }, [isOpen])

  const onHoverOption = React.useCallback(
    (index: number) => {
      if (isPointerIdle) return
      setSelectedIndex(index)
    },
    [isPointerIdle]
  )

  const onSearch = React.useCallback(
    (value: string) => {
      setSearch(value)
      setSelectedIndex(0)
      toggleState.handleOn()
    },
    [toggleState]
  )

  // ensure active item is in view
  React.useEffect(() => {
    if (!listRef.current) return
    if (!isPointerIdle) return
    const item = listRef.current.querySelector<HTMLLIElement>(
      `[${selectedOptionDataAtrribute}="true"]`
    )
    if (!item) return
    item.scrollIntoView({ block: 'nearest' })
  }, [selectedOptionDataAtrribute, selectedIndex, isPointerIdle])

  const filteredOptions = !search
    ? defaultOptions || options
    : getOptions
      ? getOptions({ options, search })
      : options.filter((option) =>
          matchesSearch([option.label, ...(option.keywords ?? [])], search)
        )

  const isEmpty = filteredOptions.length === 0

  React.useEffect(() => {
    if (!toggleState.isOn) {
      onClose?.()
    } else {
      onOpen?.()
    }
  }, [toggleState.isOn, onClose, onOpen])

  const onInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.shiftKey) return

      switch (event.key) {
        case 'ArrowUp': {
          event.preventDefault()
          let newIndex = selectedIndex - 1
          if (event.metaKey) {
            newIndex = 0
            if (listScrollerRef.current) {
              listScrollerRef.current.scrollTop = 0
            }
          }
          if (newIndex >= 0) {
            setSelectedIndex(newIndex)
          }
          break
        }
        case 'ArrowDown': {
          event.preventDefault()
          let newIndex = selectedIndex + 1
          if (event.metaKey) {
            newIndex = filteredOptions.length - 1
            if (listScrollerRef.current) {
              listScrollerRef.current.scrollTop =
                listScrollerRef.current.scrollHeight
            }
          }
          if (newIndex < filteredOptions.length) {
            setSelectedIndex(newIndex)
          }
          break
        }
        case 'Enter': {
          if (!toggleState.isOn) {
            toggleState.handleOn()
            return
          }

          const selected = filteredOptions[selectedIndex]
          if (selected) {
            onSelect(selected)
            setSearch('')
          }
          break
        }
        case 'Escape': {
          setSearch('')
          event.stopPropagation()
          toggleState.handleOff()
          break
        }
        case 'Tab': {
          setSearch('')
          event.stopPropagation()
          toggleState.handleOff()
          break
        }
        default:
          break
      }
    },
    [selectedIndex, filteredOptions, toggleState, onSelect]
  )

  return {
    search,
    onSearch,
    onInputKeyDown,
    selectedIndex,
    setSelectedIndex,
    toggleState: { ...toggleState, isOn: isOpen } satisfies ToggleState,
    onHoverOption,
    filteredOptions,
    listRef,
    isEmpty,
    getOptionProps: (index: number) => {
      return {
        [selectedOptionDataAtrribute]: index === selectedIndex,
        onMouseEnter: () => onHoverOption(index),
        onClick: () => {
          const selected = filteredOptions[index]

          if (selected) onSelect(selected)
        },
      } as const
    },
  }
}
